
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, ExternalLink, LogOut } from 'lucide-react';
import { Game } from '../../../types';
import { mockGames } from '../../../mocks/games';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Card from '../../../components/base/Card';
import Toast from '../../../components/base/Toast';
import GameFormModal from './components/GameFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

export default function AdminDashboardPage() {
  const [games, setGames] = useState<Game[]>(mockGames);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | undefined>();
  const [gameToDelete, setGameToDelete] = useState<Game | undefined>();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isAdminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
    }
  }, [navigate]);
  
  const filteredGames = games.filter(game =>
    game.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    navigate('/admin/login');
  };
  
  const handleAddGame = () => {
    setSelectedGame(undefined);
    setIsFormModalOpen(true);
  };
  
  const handleEditGame = (game: Game) => {
    setSelectedGame(game);
    setIsFormModalOpen(true);
  };
  
  const handleDeleteGame = (game: Game) => {
    setGameToDelete(game);
    setIsDeleteModalOpen(true);
  };
  
  const confirmDelete = () => {
    if (gameToDelete) {
      setGames(prev => prev.filter(g => g.id !== gameToDelete.id));
      setToast({
        message: `${gameToDelete.name}を削除しました`,
        type: 'success'
      });
      setGameToDelete(undefined);
      setIsDeleteModalOpen(false);
    }
  };
  
  const handleSaveGame = (gameData: Partial<Game>) => {
    if (selectedGame) {
      // 編集
      setGames(prev => prev.map(g => 
        g.id === selectedGame.id 
          ? { ...g, ...gameData, updatedAt: new Date().toISOString() }
          : g
      ));
      setToast({
        message: `${gameData.name}を更新しました`,
        type: 'success'
      });
    } else {
      // 新規追加
      const newGame: Game = {
        id: Date.now().toString(),
        updatedAt: new Date().toISOString(),
        ...gameData
      } as Game;
      setGames(prev => [newGame, ...prev]);
      setToast({
        message: `${gameData.name}を追加しました`,
        type: 'success'
      });
    }
    setIsFormModalOpen(false);
  };
  
  const getStatusBadge = (status: string) => {
    return status === 'published' ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        公開
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        下書き
      </span>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">管理ダッシュボード</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center space-x-1"
              >
                <ExternalLink className="h-4 w-4" />
                <span>公開サイトへ</span>
              </button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-1" />
                ログアウト
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 操作エリア */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">ゲーム管理</h2>
            <p className="text-sm text-gray-600 mt-1">
              ボードゲームの情報とルールを管理します
            </p>
          </div>
          <Button onClick={handleAddGame}>
            <Plus className="h-4 w-4 mr-2" />
            新規ゲーム追加
          </Button>
        </div>
        
        {/* 検索 */}
        <div className="mb-6">
          <Input
            placeholder="ゲーム名で検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
            className="max-w-md"
          />
        </div>
        
        {/* ゲーム一覧 */}
        {filteredGames.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-gray-500">
              {searchQuery ? (
                <>
                  <p className="text-lg font-medium mb-2">検索結果が見つかりません</p>
                  <p className="text-sm">「{searchQuery}」に一致するゲームがありません</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium mb-4">まだゲームが登録されていません</p>
                  <Button onClick={handleAddGame}>
                    <Plus className="h-4 w-4 mr-2" />
                    最初のゲームを追加
                  </Button>
                </>
              )}
            </div>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ゲーム名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      最終更新
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGames.map((game) => (
                    <tr key={game.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <img
                              className="h-10 w-10 rounded-lg object-cover object-top"
                              src={game.coverImage}
                              alt={game.name}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{game.name}</div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">{game.summary}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(game.updatedAt).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(game.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditGame(game)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGame(game)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
      
      {/* ゲーム編集/追加モーダル */}
      <GameFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSave={handleSaveGame}
        game={selectedGame}
      />
      
      {/* 削除確認モーダル */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        gameName={gameToDelete?.name || ''}
      />
      
      {/* トースト通知 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
